import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldAlert, RefreshCw, Loader, Save, Rocket, Eye, X, Languages, FileText,
  AlertTriangle, CheckCircle, Lock, Download, Fingerprint,
} from 'lucide-react';
import { SecurityDeclarationGate } from '../components/SecurityDeclaration';

// Security — the engine half of the Australia Post Parcel Contractor Security Briefing.
//
// A tab rather than a page because the loop it belongs to is the same one Safety runs:
// something the office issues, that reaches a phone in a language the driver reads, and
// that comes back as evidence. What it does NOT share is the acknowledgement — a safety
// brief is read, this is signed — so its store, its gate and its evidence live in their
// own backend (security-declaration-sb) and never mix with the safety ack trail.
//
// Three things happen here: the wording is edited (rarely, and loudly), the declaration
// is launched (which is what makes it compulsory), and the register is read.

const URL = '/.netlify/functions/security-declaration-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) })
    .then(r => r.json());

const th = { textAlign: 'left', padding: '9px 11px', fontSize: '.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', borderBottom: '1px solid var(--hp-border)' };
const td = { padding: '9px 11px', fontSize: '.83rem', borderBottom: '1px solid var(--hp-border)', verticalAlign: 'top' };
const labelStyle = { display: 'block', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 4 };

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
function humanMs(ms) {
  if (!ms || ms < 0) return '—';
  const m = Math.floor(ms / 60000), s = Math.round((ms % 60000) / 1000);
  return m ? `${m}m ${s}s` : `${s}s`;
}

const Chip = ({ text, color }) => (
  <span style={{ fontSize: '.66rem', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.05em', color, border: `1px solid ${color}`, borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{text}</span>
);

function Modal({ title, children, onClose, wide, zIndex }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: zIndex || 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ width: '100%', maxWidth: wide ? 900 : 620, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: '1.05rem', flex: 1 }}>{title}</div>
          <button className="btn btn-secondary btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function SecurityDeclarationTab({ notify }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api('admin');
    if (r.success) setData(r); else notify(r.error || 'Failed to load the security declaration', 'error');
    setLoading(false);
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const decl = data && data.declaration;
  const scope = (data && data.scope) || { isAdmin: true };

  const outstanding = useMemo(
    () => ((data && data.register) || []).filter(r => !r.signedAt),
    [data],
  );

  const openPreview = async (lang = 'en', draft) => {
    const r = await api('preview', { lang, ...(draft ? { declaration: draft } : {}) });
    if (r.success) setPreview({ ...r, lang });
    else notify(r.error || 'Preview failed', 'error');
  };

  // Launching is the loud one: it is what puts a wall in front of 54 people's morning.
  const doLaunch = async () => {
    const already = (data.register || []).filter(r => r.signedAt).length;
    const msg = decl.launch_count
      ? `RE-ISSUE this declaration?\n\nEvery driver signs again — including the ${already} who have already signed. ` +
        'Use this for the annual re-declaration.\n\nContinue?'
      : `LAUNCH this declaration?\n\nAll ${(data.register || []).length} active drivers will have to read and sign it ` +
        'before their next onboard scan. Nobody starts a round until they have.\n\nContinue?';
    if (!window.confirm(msg)) return;
    setBusy('launch');
    const r = await api('launch');
    setBusy('');
    if (r.success) { notify('Declaration launched — every driver signs before their next onboard scan', 'success'); load(); }
    else notify(r.error || 'Launch failed', 'error');
  };

  const doStandDown = async () => {
    if (!window.confirm('Stand this declaration down?\n\nNobody will be blocked. Every signature already given is kept exactly as it is.')) return;
    setBusy('stand');
    const r = await api('standDown');
    setBusy('');
    if (r.success) { notify('Stood down — the gate is clear', 'success'); load(); }
    else notify(r.error || 'Failed', 'error');
  };

  const doTranslate = async () => {
    setBusy('translate');
    const r = await api('translate');
    setBusy('');
    if (r.success) { notify(`Translated into ${r.done.join(', ')}${r.failed.length ? ` · failed: ${r.failed.join(' · ')}` : ''}`, r.failed.length ? 'warning' : 'success'); load(); }
    else notify(r.error || 'Translation failed', 'error');
  };

  const openCertificate = async (row) => {
    const r = await api('certificate', { login: row.login, recordId: row.recordId });
    if (r.success && r.url) window.open(r.url, '_blank', 'noopener');
    else notify(r.error || 'No certificate', 'error');
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={18} /> Loading…</div>;
  if (!data) return null;

  const live = data.compulsory;
  const translated = Object.keys((decl.i18n) || {});

  return (
    <div>
      {/* ── status ───────────────────────────────────────────────────────── */}
      <div className="card" style={{ padding: 18, marginBottom: 16, borderLeft: `3px solid ${live ? '#b91c1c' : 'var(--hp-border)'}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <ShieldAlert size={18} color={live ? '#b91c1c' : 'var(--hp-dim)'} />
              <span style={{ fontWeight: 800, fontSize: '1.02rem' }}>{decl.title}</span>
              <Chip text={decl.status === 'published' ? 'Published' : 'Draft'} color={decl.status === 'published' ? '#16a34a' : '#d97706'} />
              {live
                ? <Chip text="Compulsory" color="#b91c1c" />
                : <Chip text="Not gating" color="var(--hp-dim)" />}
              <Chip text={`AP form ${decl.version}`} color="var(--hp-dim)" />
            </div>
            <p style={{ fontSize: '.83rem', color: 'var(--hp-dim)', margin: '8px 0 0', lineHeight: 1.55 }}>
              {decl.source}
            </p>
            <p style={{ fontSize: '.83rem', color: 'var(--hp-dim)', margin: '4px 0 0', lineHeight: 1.55 }}>
              {decl.launched_at
                ? <>Launched {fmtWhen(decl.launched_at)}{decl.launch_count > 1 ? ` · issue ${decl.launch_count}` : ''}</>
                : 'Never launched — no driver has been asked to sign it yet.'}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: '.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>
              <Fingerprint size={12} /> {data.contentHash.slice(0, 24)}…
            </div>
          </div>

          <div style={{ textAlign: 'right', minWidth: 130 }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: data.signedCount === data.total ? '#16a34a' : '#d97706', lineHeight: 1 }}>
              {data.signedCount}<span style={{ fontSize: '1rem', color: 'var(--hp-dim)' }}>/{data.total}</span>
            </div>
            <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 4 }}>signed</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={13} /> Refresh</button>
          <button className="btn btn-secondary btn-sm" onClick={() => openPreview('en')}><Eye size={13} /> Preview as a driver</button>
          {scope.isAdmin && <button className="btn btn-secondary btn-sm" onClick={() => setEditing({ ...decl })}><Save size={13} /> Edit</button>}
          {scope.isAdmin && (
            <button className="btn btn-secondary btn-sm" onClick={doTranslate} disabled={busy === 'translate'}>
              <Languages size={13} /> {busy === 'translate' ? 'Translating…' : `Translate (${translated.length ? translated.join(', ') : 'none yet'})`}
            </button>
          )}
          {scope.isAdmin && !live && decl.status === 'published' && (
            <button className="btn btn-primary btn-sm" onClick={doLaunch} disabled={busy === 'launch'}>
              <Rocket size={13} /> {decl.launch_count ? 'Re-issue' : 'Launch'}
            </button>
          )}
          {scope.isAdmin && live && (
            <>
              <button className="btn btn-primary btn-sm" onClick={doLaunch} disabled={busy === 'launch'}>
                <Rocket size={13} /> Re-issue to everyone
              </button>
              <button className="btn btn-secondary btn-sm" onClick={doStandDown} disabled={busy === 'stand'}>
                <Lock size={13} /> Stand down
              </button>
            </>
          )}
        </div>

        {decl.status !== 'published' && scope.isAdmin && (
          <div style={{ marginTop: 12, padding: '9px 11px', borderRadius: 8, background: 'rgba(217,119,6,0.1)', color: '#d97706', fontSize: '.8rem', lineHeight: 1.5 }}>
            <AlertTriangle size={13} style={{ verticalAlign: -2 }} /> This is a draft. Tick <strong>Published</strong> in the editor before you can launch it.
          </div>
        )}
      </div>

      {/* ── the register ─────────────────────────────────────────────────── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--hp-border)' }}>
          <div style={{ fontWeight: 800, fontSize: '.95rem' }}>Signing register</div>
          <p style={{ fontSize: '.78rem', color: 'var(--hp-dim)', margin: '4px 0 0', lineHeight: 1.5 }}>
            {outstanding.length
              ? <>{outstanding.length} driver{outstanding.length > 1 ? 's have' : ' has'} not signed the current wording. Unsigned drivers are listed first.</>
              : 'Every active driver has signed the current wording.'}
          </p>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
            <thead>
              <tr>
                <th style={th}>Driver</th>
                <th style={th}>Area</th>
                <th style={th}>Signed</th>
                <th style={th}>Read in</th>
                <th style={th}>Check</th>
                <th style={th}>Time</th>
                <th style={th}>Code</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {(data.register || []).map(r => (
                <tr key={r.login}>
                  <td style={td}>
                    <div style={{ fontWeight: 700 }}>{r.name || r.login}</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{r.login}</div>
                  </td>
                  <td style={td}>{r.area || '—'}</td>
                  <td style={td}>
                    {r.signedAt
                      ? <span style={{ color: '#16a34a', fontWeight: 700 }}><CheckCircle size={12} style={{ verticalAlign: -2 }} /> {fmtDay(r.signedAt)}</span>
                      : (
                        <div>
                          <span style={{ color: '#b91c1c', fontWeight: 700 }}>Outstanding</span>
                          {/* Someone part-way through is not the same as someone who
                              has not started, and the office should be able to tell. */}
                          {r.started && (
                            <div style={{ fontSize: '.72rem', color: '#d97706', marginTop: 2 }}>
                              started · {r.started.clauses}/6 clauses, {r.started.quiz} answers
                            </div>
                          )}
                          {/* A signature against superseded wording is exactly why
                              somebody who signed last year is outstanding today. */}
                          {r.priorAt && (
                            <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 2 }}>
                              signed {fmtDay(r.priorAt)} on form {r.priorVersion}
                            </div>
                          )}
                        </div>
                      )}
                  </td>
                  <td style={td}>{r.lang ? (r.lang === 'en' ? 'English' : { es: 'Spanish', zh: 'Mandarin', mn: 'Mongolian' }[r.lang] || r.lang) : '—'}</td>
                  <td style={td}>
                    {r.signedAt
                      ? (r.quizWrong ? <span style={{ color: '#d97706' }}>{r.quizWrong} wrong</span> : <span style={{ color: 'var(--hp-dim)' }}>clean</span>)
                      : '—'}
                  </td>
                  <td style={td}>{r.signedAt ? humanMs(r.dwellMs) : '—'}</td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: '.74rem' }}>{r.verifyCode || '—'}</td>
                  <td style={td}>
                    {r.signedAt && (
                      <button className="btn btn-secondary btn-sm" onClick={() => openCertificate(r)}>
                        <Download size={12} /> PDF
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!(data.register || []).length && (
                <tr><td style={{ ...td, color: 'var(--hp-dim)' }} colSpan={8}>No active drivers in scope.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditorModal
          draft={editing} setDraft={setEditing} onClose={() => setEditing(null)}
          currentHash={data.contentHash} signedCount={data.signedCount}
          onPreview={(d, lang) => openPreview(lang, d)}
          notify={notify} onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {preview && (
        <Modal title="Preview — exactly what a driver meets" onClose={() => setPreview(null)} zIndex={260}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {(preview.declaration.langsAvailable || ['en']).map(c => (
              <button key={c} className={`btn btn-sm ${preview.lang === c ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => openPreview(c)}>{{ en: 'English', es: 'Español', zh: '中文', mn: 'Монгол' }[c] || c}</button>
            ))}
          </div>
          <SecurityDeclarationGate key={preview.lang} payload={preview} lang={preview.lang} preview />
        </Modal>
      )}
    </div>
  );
}

// ── the editor ────────────────────────────────────────────────────────────────
// Australia Post's clause TEXT is editable, because one day AP will reissue the form
// and the office must be able to follow — but it is the only field on this screen that
// is dangerous, so it says so, in place, every time. Everything else (the plain-English
// gloss, the venue, the contract numbers) is safe to reword whenever it helps.
function EditorModal({ draft, setDraft, onClose, currentHash, signedCount, onPreview, notify, onSaved }) {
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const setClause = (i, k, v) => setDraft(d => ({ ...d, clauses: d.clauses.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const setContract = (area, v) => setDraft(d => ({ ...d, contracts: { ...(d.contracts || {}), [area]: v } }));
  const setAuth = (k, v) => setDraft(d => ({ ...d, standingAuthority: { ...(d.standingAuthority || {}), [k]: v } }));

  // Compared client-side purely to warn BEFORE saving. The server recomputes it and is
  // the authority; this is here so nobody discovers the consequence afterwards.
  const hashedNow = (d) => JSON.stringify([
    (d.clauses || []).map(c => c.text),
    (d.standingAuthority || {}).allowed || '',
    ((d.standingAuthority || {}).conditions) || [],
  ]);
  const wordingChanged = useMemo(() => {
    try { return hashedNow(draft) !== (draft.__origHashed || hashedNow(draft)); }
    catch { return false; }
  }, [draft]);
  useEffect(() => { setDraft(d => (d.__origHashed ? d : { ...d, __origHashed: hashedNow(d) })); }, [setDraft]);

  const save = async () => {
    if (wordingChanged && !window.confirm(
      'You have changed wording that is part of the content fingerprint — a clause, or the '
      + 'standing authorisation.\n\n' +
      `That changes the content fingerprint, which means the ${signedCount} signature(s) already given ` +
      'no longer satisfy this declaration. Everyone will be asked to read and sign again.\n\nSave anyway?'
    )) return;
    setSaving(true);
    const { __origHashed, __origText, ...clean } = draft;
    const r = await api('save', { declaration: clean });
    setSaving(false);
    if (r.success) {
      notify(r.rehashed ? 'Saved — the wording changed, so everyone signs again' : 'Saved', r.rehashed ? 'warning' : 'success');
      onSaved();
    } else notify(r.error || 'Save failed', 'error');
  };

  return (
    <Modal title="Edit the security declaration" onClose={onClose} wide>
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.85rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={draft.status === 'published'}
              onChange={e => set('status', e.target.checked ? 'published' : 'draft')} />
            Published — drivers can be shown it
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.85rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={draft.showToDrivers !== false}
              onChange={e => set('showToDrivers', e.target.checked)} />
            Visible in the portal
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
          <div>
            <label style={labelStyle}>Where the briefing is given</label>
            <input className="input" style={{ width: '100%', boxSizing: 'border-box' }} value={draft.venue || ''}
              onChange={e => set('venue', e.target.value)} />
            <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 4 }}>Fills AP's "attended ………" line.</div>
          </div>
          <div>
            <label style={labelStyle}>Briefing given by</label>
            <input className="input" style={{ width: '100%', boxSizing: 'border-box' }} value={draft.briefedBy || ''}
              onChange={e => set('briefedBy', e.target.value)} />
          </div>
        </div>

        {/* One head contract covers every area, so this is one field. The per-area
            overrides below stay hidden unless somebody has already set one — a row of
            empty boxes invites filling in numbers that do not exist. */}
        <div>
          <label style={labelStyle}>Australia Post contract number</label>
          <input className="input" style={{ width: '100%', boxSizing: 'border-box', maxWidth: 260 }}
            value={draft.contractDefault || ''} placeholder="—"
            onChange={e => set('contractDefault', e.target.value)} />
          <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 5, lineHeight: 1.5 }}>
            Every area — DARL, City and Broadway — runs under this one contract, so it goes on
            every driver's certificate. Leave it blank and the field is left off rather than guessed.
          </div>
          {!!Object.keys(draft.contracts || {}).length && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 6 }}>
                Per-area overrides in force — an area listed here uses its own number instead.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
                {Object.keys(draft.contracts).map(area => (
                  <div key={area}>
                    <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 3 }}>{area}</div>
                    <input className="input" style={{ width: '100%', boxSizing: 'border-box' }}
                      value={draft.contracts[area] || ''} placeholder="—"
                      onChange={e => setContract(area, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* The standing authorisation. Unlike the rest of our own wording this IS in
            the content hash — clauses 1 and 2 are lawful-or-not depending on it, so
            narrowing it has to put every driver back in front of the declaration. */}
        <div style={{ border: '1px solid rgba(22,163,74,0.5)', borderRadius: 8, padding: 13 }}>
          <div style={{ fontWeight: 800, fontSize: '.88rem', marginBottom: 4, color: '#16a34a' }}>
            Standing authorisation from Australia Post
          </div>
          <p style={{ fontSize: '.78rem', color: 'var(--hp-dim)', margin: '0 0 12px', lineHeight: 1.55 }}>
            Clauses 1 and 2 both turn on whether an authorised Australia Post manager has given
            permission. This is where you state the permission that is actually in force — it shows
            on the cover, above both those clauses, and on every certificate.
            <strong> It is part of the content fingerprint:</strong> narrow it or withdraw it and every
            driver reads the new position and signs again, which is the point.
          </p>
          <label style={labelStyle}>What is permitted</label>
          <textarea className="input" rows={2} style={{ width: '100%', boxSizing: 'border-box', marginBottom: 10 }}
            value={(draft.standingAuthority || {}).allowed || ''}
            placeholder="e.g. Parcels you could not deliver may stay SECURED IN YOUR VAN overnight."
            onChange={e => setAuth('allowed', e.target.value)} />
          <label style={labelStyle}>Conditions it depends on (one per line)</label>
          <textarea className="input" rows={4} style={{ width: '100%', boxSizing: 'border-box', marginBottom: 10 }}
            value={((draft.standingAuthority || {}).conditions || []).join('\n')}
            onChange={e => setAuth('conditions', e.target.value.split('\n').map(x => x.trim()).filter(Boolean))} />
          <label style={labelStyle}>Why the conditions matter</label>
          <textarea className="input" rows={3} style={{ width: '100%', boxSizing: 'border-box' }}
            value={(draft.standingAuthority || {}).warning || ''}
            onChange={e => setAuth('warning', e.target.value)} />
          <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 6, lineHeight: 1.5 }}>
            Clear "What is permitted" and the whole panel disappears — the clauses then read as an
            unqualified prohibition, which is correct only if no permission is in force.
          </div>
        </div>

        <div>
          <label style={labelStyle}>Opening note to the driver</label>
          <textarea className="input" rows={4} style={{ width: '100%', boxSizing: 'border-box' }}
            value={draft.intro || ''} onChange={e => set('intro', e.target.value)} />
        </div>

        <div style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 14 }}>
          <div style={{ fontWeight: 800, fontSize: '.92rem', marginBottom: 4 }}>The six clauses</div>
          <p style={{ fontSize: '.78rem', color: 'var(--hp-dim)', margin: '0 0 14px', lineHeight: 1.55 }}>
            The <strong>heading</strong> and <strong>plain English</strong> are ours — reword them freely, they are
            what makes the clause understood and they do not affect any signature.
            The <strong>Australia Post wording</strong> is the instrument itself: it is what drivers sign, and changing
            one character of it invalidates every signature already given.
          </p>
          {(draft.clauses || []).map((c, i) => (
            <div key={c.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
                <span style={{ fontWeight: 800 }}>{c.n}.</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.76rem', color: 'var(--hp-dim)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!c.offence} onChange={e => setClause(i, 'offence', e.target.checked)} />
                  Its own words say a breach is a criminal offence
                </label>
              </div>
              <input className="input" style={{ width: '100%', boxSizing: 'border-box', marginBottom: 8 }}
                value={c.heading || ''} placeholder="Plain-English heading"
                onChange={e => setClause(i, 'heading', e.target.value)} />
              <textarea className="input" rows={3} style={{ width: '100%', boxSizing: 'border-box', marginBottom: 8 }}
                value={c.plain || ''} placeholder="What this means, in plain English"
                onChange={e => setClause(i, 'plain', e.target.value)} />
              <div style={{ fontSize: '.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: '#b91c1c', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                <AlertTriangle size={11} /> Australia Post wording — this is what is signed
              </div>
              <textarea className="input" rows={4} style={{ width: '100%', boxSizing: 'border-box', borderColor: 'rgba(185,28,28,0.5)' }}
                value={c.text || ''} onChange={e => setClause(i, 'text', e.target.value)} />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid var(--hp-border)', paddingTop: 14 }}>
          <button className="btn btn-secondary" onClick={() => onPreview({ ...draft, __origHashed: undefined, __origText: undefined }, 'en')}>
            <Eye size={14} /> Preview this draft
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />} Save
          </button>
        </div>
      </div>
    </Modal>
  );
}
